import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Route, Loader2, Plus, X, Calculator, MapPin, Clock, Fuel, ReceiptText, ArrowRight, Trash2, Sparkles, AlertTriangle, CalendarClock, CheckCircle2 } from "lucide-react";

const STRATEGIE = [
  { value: "lontano_primo", label: "Più lontano per primo", desc: "La squadra parte verso la città più distante, poi rientra progressivamente (consigliato)" },
  { value: "ottimizzato", label: "Ottimizzato (nearest neighbor)", desc: "Minimizza la distanza totale tra tappe consecutive" },
  { value: "vicino_primo", label: "Più vicino per primo", desc: "Inizia con la tappa più vicina alla città base" },
];

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("it-IT", { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function nextMondayISO() {
  const d = new Date();
  const offset = (8 - d.getDay()) % 7 || 7;
  d.setDate(d.getDate() + offset);
  d.setHours(7, 0, 0, 0);
  return d.toISOString().slice(0, 16);
}

export default function Itinerario() {
  const [clienti, setClienti] = useState([]);
  const [selectedCli, setSelectedCli] = useState([]);
  const [cities, setCities] = useState([""]);
  const [params, setParams] = useState({
    start_datetime: nextMondayISO(),
    strategia: "lontano_primo",
    ore_intervento: 12,
    ore_per_giorno_lavoro: 8,
    velocita_media_kmh: 80,
    rientro_base: true,
  });
  const [result, setResult] = useState(null);
  const [calc, setCalc] = useState(false);
  const [squadre, setSquadre] = useState([]);
  const [assignSquadraId, setAssignSquadraId] = useState("");
  const [overrideSquadra, setOverrideSquadra] = useState(false);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/clienti"); setClienti(data); } catch {}
      try { const s = await api.get("/squadre"); setSquadre(s.data); } catch {}
    })();
  }, []);

  const toggleCli = (id) => setSelectedCli((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const addCity = () => setCities((c) => [...c, ""]);
  const setCity = (i, v) => setCities((c) => c.map((x, idx) => idx === i ? v : x));
  const rmCity = (i) => setCities((c) => c.filter((_, idx) => idx !== i));

  const calcolaItinerario = async () => {
    const validCities = cities.map((c) => c.trim()).filter(Boolean);
    if (selectedCli.length === 0 && validCities.length === 0) {
      toast.error("Seleziona almeno un cliente o aggiungi una città");
      return;
    }
    setCalc(true);
    try {
      const { data } = await api.post("/itinerario/calcola", {
        cliente_ids: selectedCli,
        cities: validCities,
        ...params,
        ore_intervento: Number(params.ore_intervento),
        ore_per_giorno_lavoro: Number(params.ore_per_giorno_lavoro),
        velocita_media_kmh: Number(params.velocita_media_kmh),
      });
      setResult(data);
      toast.success(`Itinerario calcolato: ${data.tappe.length} tappe`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setCalc(false); }
  };

  const reset = () => { setSelectedCli([]); setCities([""]); setResult(null); };

  const [applying, setApplying] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);

  const openPreview = async () => {
    const tappeConCliente = (result?.tappe || []).filter((t) => t.cliente_id);
    if (tappeConCliente.length === 0) {
      toast.error("Nessuna tappa è collegata a un cliente");
      return;
    }
    setPreviewing(true);
    try {
      const dur = Number(params.ore_intervento) || 12;
      const { data } = await api.post("/itinerario/applica-date/preview", {
        assegnazioni: tappeConCliente.map((t) => ({
          cliente_id: t.cliente_id, data_inizio: t.arrivo_proposto, durata_ore: dur,
        })),
        squadra_id: assignSquadraId || "",
        override_squadra: overrideSquadra,
      });
      setPreview(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setPreviewing(false); }
  };

  const confirmApply = async () => {
    const tappeConCliente = (result?.tappe || []).filter((t) => t.cliente_id);
    setApplying(true);
    try {
      const dur = Number(params.ore_intervento) || 12;
      const { data } = await api.post("/itinerario/applica-date", {
        assegnazioni: tappeConCliente.map((t) => ({
          cliente_id: t.cliente_id, data_inizio: t.arrivo_proposto, durata_ore: dur,
        })),
        squadra_id: assignSquadraId || "",
        override_squadra: overrideSquadra,
      });
      toast.success(`${data.aggiornati} pianificazioni aggiornate · disponibili nel calendario`);
      if ((data.saltati || []).length > 0) {
        toast.warning(`${data.saltati.length} client${data.saltati.length === 1 ? "e" : "i"} saltat${data.saltati.length === 1 ? "o" : "i"}`);
      }
      setPreview(null);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setApplying(false); }
  };

  return (
    <div data-testid="itinerario-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
        <div>
          <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest mb-2" style={{ color: "var(--primary)" }}>
            <Sparkles className="w-3.5 h-3.5" /> Pre-pianificazione
          </div>
          <h1 className="text-3xl font-extrabold tracking-tight" style={{ fontFamily: "Manrope" }}>Itinerario consigliato</h1>
          <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>
            Inserisci le tappe (clienti o città) → il sistema calcola l'ordine ottimale, le date proposte e i costi.
          </p>
        </div>
        {result && <button onClick={reset} className="btn-secondary" data-testid="iti-reset"><Trash2 className="w-4 h-4" /> Nuovo calcolo</button>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        {/* SELECT CLIENTI */}
        <div className="card-shell p-5">
          <h3 className="font-bold mb-2" style={{ fontFamily: "Manrope" }}>Clienti potenziali</h3>
          <p className="text-xs mb-3" style={{ color: "var(--text-muted)" }}>
            Spunta i clienti da includere. Anche senza data appuntamento ancora confermata.
          </p>
          {clienti.length === 0 ? (
            <div className="text-xs p-2" style={{ color: "var(--text-muted)" }}>Nessun cliente disponibile</div>
          ) : (
            <div className="max-h-72 overflow-y-auto border rounded-md" style={{ borderColor: "var(--border)" }}>
              {clienti.map((c) => (
                <label key={c.id} className="flex items-center gap-2 p-2 border-b cursor-pointer hover:bg-black/5 text-sm"
                  style={{ borderColor: "var(--border)" }} data-testid={`iti-cli-${c.id}`}>
                  <input type="checkbox" checked={selectedCli.includes(c.id)} onChange={() => toggleCli(c.id)}
                    data-testid={`iti-cli-cb-${c.id}`} />
                  <span className="flex-1">
                    <span className="font-semibold">{c.nome} {c.cognome_ragione_sociale}</span>
                    <span className="ml-2 text-xs" style={{ color: "var(--text-muted)" }}>
                      <MapPin className="w-3 h-3 inline" /> {c.citta || "—"} {c.provincia && `(${c.provincia})`}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          )}
          <div className="text-xs mt-2" style={{ color: "var(--text-muted)" }}>
            Selezionati: <strong>{selectedCli.length}</strong>
          </div>
        </div>

        {/* CITTÀ MANUALI */}
        <div className="card-shell p-5">
          <h3 className="font-bold mb-2" style={{ fontFamily: "Manrope" }}>Città / località aggiuntive</h3>
          <p className="text-xs mb-3" style={{ color: "var(--text-muted)" }}>
            Aggiungi località senza un cliente associato (sopralluoghi commerciali, fiere, ecc.).
          </p>
          <div className="space-y-2">
            {cities.map((c, i) => (
              <div key={i} className="flex gap-2">
                <input value={c} onChange={(e) => setCity(i, e.target.value)}
                  placeholder="Es. Bologna, Firenze, Bari…"
                  className="flex-1 px-3 py-2 text-sm rounded-md border outline-none"
                  style={{ borderColor: "var(--border)", background: "#fff" }}
                  data-testid={`iti-city-${i}`} />
                {cities.length > 1 && (
                  <button onClick={() => rmCity(i)} className="p-2 rounded hover:bg-black/5" data-testid={`iti-rm-city-${i}`}>
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
            <button onClick={addCity} className="btn-secondary text-xs" data-testid="iti-add-city">
              <Plus className="w-3.5 h-3.5" /> Aggiungi città
            </button>
          </div>
        </div>
      </div>

      {/* PARAMETRI */}
      <div className="card-shell p-5 mb-4">
        <h3 className="font-bold mb-3" style={{ fontFamily: "Manrope" }}>Parametri turno</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <Field label="Partenza (lunedì)" type="datetime-local" value={params.start_datetime}
            onChange={(v) => setParams({ ...params, start_datetime: v })} testid="iti-start" />
          <Field label="Ore intervento" type="number" step="0.5" min="0.5" value={params.ore_intervento}
            onChange={(v) => setParams({ ...params, ore_intervento: v })} testid="iti-ore-int" helper="12 = 1.5 giorni" />
          <Field label="Ore lavoro / gg" type="number" step="0.5" min="1" value={params.ore_per_giorno_lavoro}
            onChange={(v) => setParams({ ...params, ore_per_giorno_lavoro: v })} testid="iti-ore-gg" />
          <Field label="Vel. media (km/h)" type="number" step="5" min="20" value={params.velocita_media_kmh}
            onChange={(v) => setParams({ ...params, velocita_media_kmh: v })} testid="iti-vel" />
          <div className="md:col-span-2">
            <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Strategia ordinamento</label>
            <select value={params.strategia} onChange={(e) => setParams({ ...params, strategia: e.target.value })}
              className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none"
              style={{ borderColor: "var(--border)", background: "#fff" }}
              data-testid="iti-strategia">
              {STRATEGIE.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
              {STRATEGIE.find((s) => s.value === params.strategia)?.desc}
            </div>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={params.rientro_base}
              onChange={(e) => setParams({ ...params, rientro_base: e.target.checked })}
              data-testid="iti-rientro" />
            Rientro alla città base a fine itinerario
          </label>
          <button onClick={calcolaItinerario} disabled={calc} className="btn-primary disabled:opacity-50" data-testid="iti-calc">
            {calc ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calculator className="w-4 h-4" />}
            Calcola itinerario
          </button>
        </div>
      </div>

      {/* RISULTATO */}
      {result && (
        <div className="space-y-4" data-testid="iti-result">
          {/* KPI totali */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <KPI icon={Sparkles} label="Tappe" value={result.totali.tappe} testid="iti-kpi-tappe" />
            <KPI icon={Route} label="Km totali" value={`${result.totali.km_totali.toLocaleString("it-IT")}`} sub="km" testid="iti-kpi-km" />
            <KPI icon={Clock} label="Durata" value={`${result.totali.durata_giorni}`} sub="giorni" testid="iti-kpi-durata" />
            <KPI icon={Fuel} label="Carburante" value={`€ ${result.totali.costo_carburante.toFixed(0)}`} sub={`+ €${result.totali.costo_caselli.toFixed(0)} caselli`} testid="iti-kpi-fuel" />
            <KPI icon={ReceiptText} label="Costo totale" value={`€ ${result.totali.costo_totale.toFixed(2)}`} sub="trasferta" highlight testid="iti-kpi-total" />
          </div>

          {/* Strategy info */}
          <div className="text-sm flex items-center gap-2" style={{ color: "var(--text-muted)" }}>
            <Sparkles className="w-4 h-4" />
            Strategia: <strong>{STRATEGIE.find((s) => s.value === result.strategia)?.label}</strong> ·
            Partenza da <strong>{result.citta_base.label}</strong> il {fmtDateTime(result.start_datetime)}
          </div>

          {/* Timeline tappe */}
          <div className="space-y-2">
            {result.tappe.map((t) => (
              <div key={t.ordine} className="card-shell p-4 flex flex-col md:flex-row gap-4" data-testid={`iti-tappa-${t.ordine}`}>
                <div className="flex md:flex-col items-center justify-center md:w-20 md:border-r" style={{ borderColor: "var(--border)" }}>
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-lg" style={{ background: "var(--primary)" }}>
                    {t.ordine}
                  </div>
                  <div className="md:mt-2 text-xs ml-2 md:ml-0" style={{ color: "var(--accent)", fontWeight: 700 }}>
                    {t.distanza_km.toFixed(0)}<span className="text-xs">km</span>
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between flex-wrap gap-1">
                    {t.cliente_id ? (
                      <Link to={`/clienti/${t.cliente_id}`} className="font-bold text-base hover:underline">
                        {t.label}
                      </Link>
                    ) : (
                      <span className="font-bold text-base">{t.label}</span>
                    )}
                    <span className="text-xs" style={{ color: "var(--text-muted)" }}>
                      <MapPin className="w-3 h-3 inline" /> {t.city_label}
                    </span>
                  </div>
                  <div className="text-xs mt-1 inline-flex items-center gap-1" style={{ color: "var(--text-muted)" }}>
                    <span>da {t.da_label}</span>
                    <ArrowRight className="w-3 h-3" />
                    <span>{t.tempo_guida_ore}h di guida</span>
                  </div>
                  <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 p-2 rounded-md text-xs" style={{ background: "var(--surface-muted)" }}>
                    <div className="inline-flex items-center gap-1.5">
                      <CalendarClock className="w-3.5 h-3.5" style={{ color: "#16A34A" }} />
                      <strong>Arrivo:</strong> {fmtDateTime(t.arrivo_proposto)}
                    </div>
                    <div className="inline-flex items-center gap-1.5">
                      <CalendarClock className="w-3.5 h-3.5" style={{ color: "#B45309" }} />
                      <strong>Fine intervento:</strong> {fmtDateTime(t.fine_intervento)}
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <span className="text-xs px-2 py-0.5 rounded-full inline-flex items-center gap-1" style={{ background: "#FFF4E5", color: "#7A4500" }}>
                      <Fuel className="w-3 h-3" /> €{t.costo_tratta.costo_carburante.toFixed(2)}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded-full inline-flex items-center gap-1" style={{ background: "#F3E8FF", color: "#6B21A8" }}>
                      <ReceiptText className="w-3 h-3" /> €{t.costo_tratta.costo_caselli.toFixed(2)}
                    </span>
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: "var(--primary)", color: "#fff" }}>
                      Tratta €{t.costo_tratta.totale.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
            {result.rientro_base && (
              <div className="card-shell p-4 flex items-center gap-4" style={{ background: "#F1F8E9", borderLeft: "4px solid #16A34A" }} data-testid="iti-rientro-card">
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold" style={{ background: "#16A34A" }}>
                  ⌂
                </div>
                <div className="flex-1">
                  <div className="font-bold">Rientro a {result.citta_base.label}</div>
                  <div className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>
                    {result.rientro_base.distanza_km.toFixed(0)} km · {result.rientro_base.tempo_guida_ore}h di guida ·
                    arrivo {fmtDateTime(result.rientro_base.arrivo_base)} · costo €{result.rientro_base.costo.totale.toFixed(2)}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="card-shell p-4 text-xs flex items-start gap-3" style={{ background: "#FFF8E1", color: "#7A4500" }}>
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              Le distanze sono stime stradali (Haversine × {1.3}) — verificale con un router reale per turni critici.
              {result.tappe.some((t) => t.cliente_id) ? (
                <div className="mt-2">
                  <strong>Suggerimento:</strong> usa il bottone qui sotto per scrivere automaticamente le date proposte sulle pianificazioni dei clienti selezionati.
                </div>
              ) : (
                <div className="mt-1">Le tappe attuali sono solo città manuali — non collegate a clienti, non possono essere applicate.</div>
              )}
            </div>
          </div>

          {result.tappe.some((t) => t.cliente_id) && (
            <div className="card-shell p-4 flex flex-col md:flex-row gap-3 md:items-end justify-between" data-testid="iti-apply-bar">
              <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>
                    Squadra da assegnare (opzionale)
                  </label>
                  <select value={assignSquadraId} onChange={(e) => setAssignSquadraId(e.target.value)}
                    className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none"
                    style={{ borderColor: "var(--border)", background: "#fff" }}
                    data-testid="iti-assign-squadra">
                    <option value="">— Non assegnare squadra —</option>
                    {squadre.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                {assignSquadraId && (
                  <label className="flex items-center gap-2 text-sm cursor-pointer self-end pb-2" data-testid="iti-override-label">
                    <input type="checkbox" checked={overrideSquadra} onChange={(e) => setOverrideSquadra(e.target.checked)}
                      data-testid="iti-override-cb" />
                    Sovrascrivi squadra esistente sui clienti
                  </label>
                )}
              </div>
              <button onClick={openPreview} disabled={previewing} className="btn-primary disabled:opacity-50" data-testid="iti-apply">
                {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                Applica date proposte ({result.tappe.filter((t) => t.cliente_id).length})
              </button>
            </div>
          )}
        </div>
      )}

      {/* Preview Modal */}
      {preview && (
        <div className="fixed inset-0 z-50 flex items-start md:items-center justify-center p-4 bg-black/50 overflow-y-auto" onClick={() => setPreview(null)}>
          <div className="card-shell w-full max-w-4xl my-auto bg-white" onClick={(e) => e.stopPropagation()} data-testid="iti-preview-modal">
            <div className="p-5 border-b flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
              <div>
                <h2 className="text-xl font-bold" style={{ fontFamily: "Manrope" }}>Anteprima modifiche</h2>
                <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
                  {preview.totale} client{preview.totale === 1 ? "e" : "i"} ·
                  {" "}<strong style={{ color: "#16A34A" }}>{preview.creati}</strong> creazion{preview.creati === 1 ? "e" : "i"} ·
                  {" "}<strong style={{ color: "#0D47A1" }}>{preview.aggiornati}</strong> aggiornament{preview.aggiornati === 1 ? "o" : "i"}
                  {preview.saltati > 0 && <> · <strong style={{ color: "#B30000" }}>{preview.saltati}</strong> saltat{preview.saltati === 1 ? "o" : "i"}</>}
                </div>
              </div>
              <button onClick={() => setPreview(null)} className="p-1.5 rounded hover:bg-black/5"><X className="w-4 h-4" /></button>
            </div>

            <div className="p-5 max-h-[60vh] overflow-y-auto">
              <div className="space-y-3">
                {preview.changes.map((c, i) => (
                  <div key={i} className="p-3 rounded-md border" style={{ borderColor: "var(--border)" }} data-testid={`iti-prev-row-${i}`}>
                    <div className="flex items-baseline justify-between gap-2 mb-2">
                      <div className="font-semibold">
                        {c.cliente_name || c.cliente_id}
                        {c.cliente_ref && <span className="text-xs ml-2 font-mono" style={{ color: "var(--text-muted)" }}>{c.cliente_ref}</span>}
                      </div>
                      <ActionBadge action={c.action} motivo={c.motivo} />
                    </div>
                    {c.action !== "saltato" && (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                        <DiffField label="Data inizio" oldVal={fmtDateTime(c.data_old) || "—"} newVal={fmtDateTime(c.data_new)} changed={c.data_changed} />
                        <DiffField label="Durata (h)" oldVal={c.durata_old ?? "—"} newVal={c.durata_new} changed={c.durata_changed} />
                        <DiffField label="Squadra" oldVal={c.squadra_old_name || "—"} newVal={c.squadra_new_name || "—"} changed={c.squadra_changed} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="p-5 border-t flex items-center justify-between gap-2" style={{ borderColor: "var(--border)" }}>
              <div className="text-xs" style={{ color: "var(--text-muted)" }}>
                I campi non evidenziati restano invariati. I clienti saltati non verranno modificati.
              </div>
              <div className="flex gap-2">
                <button onClick={() => setPreview(null)} className="btn-secondary" data-testid="iti-cancel-apply">Annulla</button>
                <button onClick={confirmApply} disabled={applying || preview.totale - preview.saltati === 0}
                  className="btn-primary disabled:opacity-50" data-testid="iti-confirm-apply">
                  {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  Conferma e applica ({preview.totale - preview.saltati})
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, type = "text", value, onChange, testid, helper, step, min, max }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>{label}</label>
      <input type={type} step={step} min={min} max={max} value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none"
        style={{ borderColor: "var(--border)", background: "#fff" }}
        data-testid={testid} />
      {helper && <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>{helper}</div>}
    </div>
  );
}

function KPI({ icon: Icon, label, value, sub, highlight, testid }) {
  return (
    <div className="card-shell p-3 flex items-center gap-3" style={{ background: highlight ? "var(--primary)" : "#fff", color: highlight ? "#fff" : "inherit" }} data-testid={testid}>
      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: highlight ? "rgba(255,255,255,0.15)" : "var(--surface-muted)", color: highlight ? "#fff" : "var(--primary)" }}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider font-semibold opacity-80">{label}</div>
        <div className="text-lg font-extrabold leading-tight" style={{ fontFamily: "Manrope" }}>{value}</div>
        {sub && <div className="text-[10px] opacity-70">{sub}</div>}
      </div>
    </div>
  );
}

function ActionBadge({ action, motivo }) {
  const styles = {
    creato: { bg: "#E6F4EA", fg: "#1B5E20", label: "Creato" },
    aggiornato: { bg: "#E3F2FD", fg: "#0D47A1", label: "Aggiornato" },
    saltato: { bg: "#FDECEA", fg: "#B30000", label: "Saltato" },
  };
  const s = styles[action] || styles.aggiornato;
  return (
    <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: s.bg, color: s.fg }} title={motivo}>
      {s.label}{motivo ? ` · ${motivo}` : ""}
    </span>
  );
}

function DiffField({ label, oldVal, newVal, changed }) {
  return (
    <div className={`p-2 rounded ${changed ? "border-l-4" : ""}`} style={{ background: changed ? "#FFF8E1" : "var(--surface-muted)", borderColor: changed ? "#F59E0B" : "transparent" }}>
      <div className="text-[10px] uppercase tracking-wider font-semibold opacity-70">{label}</div>
      {changed ? (
        <div className="flex items-center gap-1 mt-0.5 flex-wrap">
          <span className="line-through opacity-60">{String(oldVal)}</span>
          <ArrowRight className="w-3 h-3" />
          <span className="font-bold">{String(newVal)}</span>
        </div>
      ) : (
        <div className="mt-0.5" style={{ color: "var(--text-muted)" }}>{String(newVal)}</div>
      )}
    </div>
  );
}
